import celfinetLogo  from '../../../../assets/Cyient_Slate_Std_RGB.png'
import { useRef, useState, useEffect } from 'react'
import { Box, Button, Dialog, DialogContent, IconButton, Slider, Tooltip, Typography } from '@mui/material'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import CloseIcon      from '@mui/icons-material/Close'
import DownloadIcon   from '@mui/icons-material/Download'
import TuneIcon       from '@mui/icons-material/Tune'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import { Line } from 'react-chartjs-2'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectBeacon4VisibleSeries,
  toggleBeacon4Series,
  resetBeacon4HiddenSeries,
} from '../../reducers/Beacon4FiltersReducer'
import { selectBeacon4RagMap } from '../../reducers/Beacon4RagReducer'

/**
 * Small "↻" icon button that resets a single slider back to [0, 100] (the full
 * data range). Used inline next to each slider label so the user can clear that
 * one axis without touching the others.
 */
function ResetButton({ onClick, disabled, title = 'Reset to default range' }) {
  return (
    <Tooltip title={title}>
      <span>
        <IconButton
          size="small"
          onClick={onClick}
          disabled={disabled}
          sx={{ p: 0.25, ml: 0.25, color: disabled ? 'text.disabled' : 'text.secondary',
                '&:hover': { color: 'primary.main' } }}
        >
          <RestartAltIcon sx={{ fontSize: 13 }} />
        </IconButton>
      </span>
    </Tooltip>
  )
}

const COLOR_MAP = {
  '2G':     '#1a3a6e',
  '3G':     '#4fc3f7',
  '4G':     '#f57c00',
  '5G':     '#ffb74d',
  'Blended':'#212121',
  'value':  '#1565c0',
}

const SERIES_DEFS = [
  { key: '2G',       color: '#1a3a6e', dash: false },
  { key: '3G',       color: '#4fc3f7', dash: false },
  { key: '4G',       color: '#f57c00', dash: false },
  { key: '5G',       color: '#ffb74d', dash: false },
  { key: 'Blended',  color: '#212121', dash: true  },
  { key: 'RAG Green',color: '#2e7d32', dash: true  },
  { key: 'RAG Red',  color: '#c62828', dash: true  },
]


// Map chart title prefix to RAG field names
const RAG_KEY_MAP = {
  avail: { green: 'availGreen', red: 'availRed' },
  vssr:  { green: 'vssrGreen',  red: 'vssrRed'  },
  dssr:  { green: 'dssrGreen',  red: 'dssrRed'  },
  vdcr:  { green: 'vdcrGreen',  red: 'vdcrRed'  },
  ddcr:  { green: 'ddcrGreen',  red: 'ddcrRed'  },
}

function buildRagDatasets(ragMap, labels, kpiKey, visibleSeries = null) {
  const fields = RAG_KEY_MAP[kpiKey]
  if (!fields || !Object.keys(ragMap).length) return []

  // For each label (date), find the corresponding month in ragMap
  const greenData = labels.map(label => {
    const monthKey = label.slice(0, 7)  // 'YYYY-MM'
    return ragMap[monthKey]?.[fields.green] ?? null
  })
  const redData = labels.map(label => {
    const monthKey = label.slice(0, 7)
    return ragMap[monthKey]?.[fields.red] ?? null
  })

  // Only add if we have at least some values
  const hasGreen = greenData.some(v => v != null)
  const hasRed   = redData.some(v => v != null)
  const datasets = []

  if (hasGreen) datasets.push({
    label: 'RAG Green', data: greenData,
    borderColor: '#2e7d32', borderWidth: 1.5, borderDash: [6, 3],
    pointRadius: 0, lineTension: 0, fill: false,
    hidden: visibleSeries !== null && !visibleSeries.includes('RAG Green'),
  })
  if (hasRed) datasets.push({
    label: 'RAG Red', data: redData,
    borderColor: '#c62828', borderWidth: 1.5, borderDash: [6, 3],
    pointRadius: 0, lineTension: 0, fill: false,
    hidden: visibleSeries !== null && !visibleSeries.includes('RAG Red'),
  })
  return datasets
}

// Group series by unit so we can decide if a second axis is needed.
// % goes to the left axis, anything else (#, erl, MB, ...) goes to the right.
function isPctUnit(u) { return (u ?? '').trim() === '%' }

// Context series rendered as filled area: Traffic (left panel) and Availability (right panel)
function isContextSeries(label) {
  const l = (label ?? '').toLowerCase()
  return l.includes('traffic') || l.includes('availability')
}

// Convert hex colour to rgba with given alpha (for area fill)
function hexToRgba(hex, alpha) {
  if (!hex || !hex.startsWith('#') || hex.length !== 7) return `rgba(21,101,192,${alpha})`
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r},${g},${b},${alpha})`
}

function buildDatasets(series, visibleSeries) {
  return series.map(s => {
    const color  = s.color ?? COLOR_MAP[s.key] ?? '#1565c0'
    const isCtx  = isContextSeries(s.key)
    return {
      label:           s.key,
      data:            [...(s.data ?? [])],
      unit:            s.unit ?? '',
      // Prefer the colour provided on the series object (Performance), fall back to the global COLOR_MAP (Blended)
      borderColor:     color,
      backgroundColor: isCtx ? hexToRgba(color, 0.18) : 'transparent',
      borderWidth:     s.key === 'Blended' ? 2.5 : (isCtx ? 1 : 1.5),
      borderDash:      s.key === 'Blended' ? [6, 3] : [],
      pointRadius:     0,
      lineTension:     0,
      fill:            isCtx ? 'origin' : false,
      hidden:          visibleSeries !== null && !visibleSeries.includes(s.key),
      yAxisID:         isPctUnit(s.unit) ? 'y-pct' : 'y-other',
      // Draw context areas below the line series so they don't cover them
      order:           isCtx ? 2 : 1,
    }
  })
}

function buildOptions(unit, yBounds = {}, pinnedActive = false, datasets = []) {
  const { pctMin, pctMax, otherMin, otherMax } = yBounds
  // Discover which axes are actually used by the datasets (look at yAxisID)
  // Only consider VISIBLE datasets so hiding the last % series collapses to a single-axis chart
  const visible = datasets.filter(d => !d.hidden)
  const usedAxes = new Set(visible.map(d => d.yAxisID).filter(Boolean))
  const hasPctAxis   = usedAxes.has('y-pct')
  const hasOtherAxis = usedAxes.has('y-other')
  const dualAxis     = hasPctAxis && hasOtherAxis

  // Build axis labels dynamically from the actual series units. If a single
  // axis is sharing multiple units (e.g. erl + MB), join them with " · ".
  const uniqueUnits = (axisId) => [...new Set(
    visible.filter(d => d.yAxisID === axisId).map(d => d.unit ?? '').filter(Boolean)
  )]
  const pctUnitsLabel   = uniqueUnits('y-pct').join(' · ')   || '%'
  const otherUnitsLabel = uniqueUnits('y-other').join(' · ') || '#'

  const yAxes = []
  if (hasPctAxis) {
    yAxes.push({
      id:        'y-pct',
      position:  'left',
      ticks: {
        fontSize:      9,
        maxTicksLimit: 6,
        ...(pctMin != null ? { min: pctMin } : {}),
        ...(pctMax != null ? { max: pctMax } : {}),
        callback: v => Number(v).toFixed(2),
      },
      gridLines: { color: 'rgba(0,0,0,.05)' },
      scaleLabel: dualAxis ? { display: true, labelString: pctUnitsLabel, fontSize: 9, fontColor: '#8fa3bc' } : { display: false },
    })
  }
  if (hasOtherAxis) {
    yAxes.push({
      id:        'y-other',
      position:  dualAxis ? 'right' : 'left',
      ticks: {
        fontSize:      9,
        maxTicksLimit: 6,
        ...(otherMin != null ? { min: otherMin } : {}),
        ...(otherMax != null ? { max: otherMax } : {}),
        callback: v => Number(v).toLocaleString(),
      },
      gridLines: { display: false },
      scaleLabel: dualAxis ? { display: true, labelString: otherUnitsLabel, fontSize: 9, fontColor: '#8fa3bc' } : { display: false },
    })
  }
  // Default fallback (no datasets yet) — single axis, format with unit suffix
  if (yAxes.length === 0) {
    // Use the percentage bounds as a generic default since most KPIs are %
    yAxes.push({
      ticks: {
        fontSize:      9,
        maxTicksLimit: 6,
        ...(pctMin != null ? { min: pctMin } : {}),
        ...(pctMax != null ? { max: pctMax } : {}),
        callback: v => Number(v).toFixed(2),
      },
      gridLines: { color: 'rgba(0,0,0,.05)' },
    })
  }

  return {
    responsive:          true,
    maintainAspectRatio: false,
    animation:           { duration: 300 },
    legend:              { display: false },
    tooltips: {
      mode:            'index',
      intersect:       false,
      backgroundColor: 'rgba(255,255,255,0.97)',
      borderColor:     'rgba(0,0,0,0.1)',
      borderWidth:     1,
      titleFontColor:  '#8fa3bc',
      titleFontSize:   10,
      bodyFontColor:   '#1a2233',
      bodyFontSize:    11,
      xPadding:        10,
      yPadding:        10,
      caretSize:       5,
      cornerRadius:    6,
      callbacks: {
        label: (item, data) => {
          const ds       = data.datasets[item.datasetIndex]
          const dsUnit   = ds.unit ?? unit ?? ''
          const value    = Number(item.yLabel)
          const formatted = isPctUnit(dsUnit) ? value.toFixed(2) : value.toLocaleString(undefined, { maximumFractionDigits: 2 })
          return ` ${ds.label}: ${formatted}${dsUnit}`
        },
        labelColor: (item, chart) => {
          const ds = chart.data.datasets[item.datasetIndex]
          return { borderColor: ds.borderColor, backgroundColor: ds.borderColor }
        },
      },
    },
    scales: {
      xAxes: [{ ticks: { fontSize: 9, maxRotation: 45, autoSkip: true, maxTicksLimit: 12 }, gridLines: { display: false } }],
      yAxes,
    },
  }
}

function LegendRow({ d, visibleSeries, onToggle }) {
  const isHidden = visibleSeries !== null && !visibleSeries.includes(d.key)
  return (
    <Box onClick={() => onToggle(d.key)} sx={{
      display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5,
      borderRadius: 1, cursor: 'pointer',
      opacity: isHidden ? 0.35 : 1, transition: 'all 0.15s',
      '&:hover': { bgcolor: 'action.hover' },
    }}>
      <Box sx={{
        width: 22, flexShrink: 0,
        height: d.dash ? 0 : 2.5, borderRadius: 1,
        bgcolor: d.dash ? 'transparent' : d.color,
        borderTop: d.dash ? `2px dashed ${d.color}` : 'none',
      }} />
      <Typography sx={{ fontSize: 11, fontWeight: isHidden ? 400 : 600, color: isHidden ? 'text.disabled' : d.color }}>
        {d.key}
      </Typography>
    </Box>
  )
}

const TECH_DEFS = SERIES_DEFS.filter(d => !d.key.startsWith('RAG'))
const RAG_DEFS  = SERIES_DEFS.filter(d =>  d.key.startsWith('RAG'))

function ModalLegend({ series, visibleSeries, onToggle, onClear, hasRag }) {
  const techKeys = new Set(series.map(s => s.key))
  const techDefs = TECH_DEFS.filter(d => techKeys.has(d.key))

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 120, pl: 2, borderLeft: '1px solid', borderColor: 'divider' }}>

      <Typography sx={{ fontSize: 9, fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.5 }}>
        Technologies
      </Typography>
      {techDefs.map(d => <LegendRow key={d.key} d={d} visibleSeries={visibleSeries} onToggle={onToggle} />)}

      {hasRag && (
        <>
          <Typography sx={{ fontSize: 9, fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.08em', mt: 1.5, mb: 0.5 }}>
            RAG Thresholds
          </Typography>
          {RAG_DEFS.map(d => <LegendRow key={d.key} d={d} visibleSeries={visibleSeries} onToggle={onToggle} />)}
        </>
      )}

      <Box sx={{ mt: 1 }}>
        {visibleSeries !== null ? (
          <Box onClick={onClear} sx={{
            fontSize: 10, fontWeight: 600, color: 'primary.main', cursor: 'pointer',
            textAlign: 'center', py: 0.5, borderRadius: 1,
            border: '1px solid', borderColor: 'primary.light',
            '&:hover': { bgcolor: 'primary.light' }, transition: 'all .15s',
          }}>
            ✕ Show all
          </Box>
        ) : (
          <Typography sx={{ fontSize: 9, color: 'text.disabled', textAlign: 'center', fontStyle: 'italic' }}>
            Click to isolate
          </Typography>
        )}
      </Box>
    </Box>
  )
}

export default function Beacon4KpiLineChart({ title, seriesDto, height = 220, kpiKey }) {
  const fillHeight = height === '100%'
  const dispatch     = useDispatch()
  const visibleSeries = useSelector(selectBeacon4VisibleSeries)
  const ragMap       = useSelector(selectBeacon4RagMap)
  const [open,          setOpen]          = useState(false)
  const [range,         setRange]         = useState([0, 100])
  const [yRange,        setYRange]        = useState([0, 100])
  const [y2Range,       setY2Range]       = useState([0, 100])
  const [sliders,       setSliders]       = useState(false)
  const [pinnedTooltip,      setPinnedTooltip]      = useState(null)
  const [modalPinnedTooltip, setModalPinnedTooltip] = useState(null)
  const modalChartRef    = useRef(null)
  const modalChartBoxRef = useRef(null)
  const modalLineRef     = useRef(null)
  const chartRef         = useRef(null)
  const chartBoxRef      = useRef(null)

  // Force Chart.js to resize when the container resizes (grid layouts often
  // change width without firing window.resize, leaving Chart.js stale).
  // We wrap the resize in requestAnimationFrame and compare last-seen
  // dimensions to break the classic "ResizeObserver loop completed with
  // undelivered notifications" feedback — Chart.js's resize() can itself
  // mutate the box, which would otherwise fire the observer again.
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return
    const el = chartBoxRef.current
    if (!el) return
    let raf = 0
    let lastW = 0, lastH = 0
    const ro = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect
      if (!rect) return
      // Only react when the size actually changed (>0.5px to absorb sub-pixel
      // rounding from flex/clamp containers).
      if (Math.abs(rect.width - lastW) < 0.5 && Math.abs(rect.height - lastH) < 0.5) return
      lastW = rect.width
      lastH = rect.height
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        const ci = chartRef.current?.chartInstance
        if (ci) try { ci.resize() } catch (_) {}
      })
    })
    ro.observe(el)
    return () => { cancelAnimationFrame(raf); ro.disconnect() }
  }, [])

  // Reset Y sliders whenever the set of series changes (e.g. user toggled the
  // legend). Without this, a zoomed-in slider keeps clipping the axis even
  // after the underlying data bounds have shifted.
  const seriesSignature = (seriesDto?.series ?? []).map(s => s.key).join('|')
  useEffect(() => {
    setYRange([0, 100])
    setY2Range([0, 100])
  }, [seriesSignature])


  const exportChartPDF = async () => {
    const el = modalChartRef.current
    if (!el) return
    const html2canvas = (await import('html2canvas')).default
    const { jsPDF }   = await import('jspdf')
    const canvas = await html2canvas(el, { scale: 3, backgroundColor: '#ffffff', logging: false })
    const w = canvas.width / 2, h = canvas.height / 2
    const pdf = new jsPDF({ orientation: 'landscape', unit: 'px', format: [w, h] })
    pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, w, h)
    pdf.save(`${title.replace(/[^a-z0-9]/gi, '_')}.pdf`)
  }

  const handleToggle = (key) => dispatch(toggleBeacon4Series(key))
  const handleRangeChange = (v) => { setRange(v); setPinnedTooltip(null) }

  // Reset helpers: each slider has its own back-to-default action, plus a
  // "Reset all" that brings every axis back to the full data range in one shot.
  // The default for every slider is [0, 100] (i.e. 0%-100% of the observed
  // data range), so reset means simply restoring that pair.
  const isXReset  = range[0]   === 0 && range[1]   === 100
  const isY1Reset = yRange[0]  === 0 && yRange[1]  === 100
  const isY2Reset = y2Range[0] === 0 && y2Range[1] === 100
  const allReset  = isXReset && isY1Reset && isY2Reset
  const resetX    = () => { setRange([0, 100]); setPinnedTooltip(null); setModalPinnedTooltip(null) }
  const resetY1   = () => setYRange([0, 100])
  const resetY2   = () => setY2Range([0, 100])
  const resetAll  = () => { resetX(); resetY1(); resetY2() }

  if (!seriesDto) {
    return (
      <Box sx={{ bgcolor: '#fff', border: '1px solid #e3e8ef', borderRadius: '12px', p: '12px 14px', boxShadow: '0 1px 3px rgba(0,0,0,.04)' }}>
        <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#4a5568', textAlign: 'center', mb: 1 }}>{title}</Typography>
        <Box sx={{ height, bgcolor: '#f7f8fa', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography sx={{ fontSize: 11, color: '#8fa3bc' }}>Loading…</Typography>
        </Box>
      </Box>
    )
  }

  const { labels, series, unit } = seriesDto
  const ragDatasets = kpiKey ? buildRagDatasets(ragMap, labels ?? [], kpiKey, visibleSeries) : []
  const datasets    = [...buildDatasets(series, visibleSeries), ...ragDatasets]

  // Slice by X range
  const totalPts       = (labels ?? []).length
  const startIdx       = Math.floor(range[0] / 100 * totalPts)
  const endIdx         = Math.ceil(range[1]  / 100 * totalPts)
  const slicedLabels   = [...(labels ?? [])].slice(startIdx, endIdx)
  const slicedDatasets = datasets.map(d => ({ ...d, data: (d.data ?? []).slice(startIdx, endIdx) }))

  // Y bounds — computed PER AXIS so each slider controls its own data range
  function axisBounds(datasets, axisId) {
    const vals = datasets
      .filter(d => !d.hidden && d.yAxisID === axisId)
      .flatMap(d => (d.data ?? []).filter(v => v != null))
    if (vals.length === 0) return null
    const min = vals.reduce((a, b) => a < b ? a : b, vals[0])
    const max = vals.reduce((a, b) => a > b ? a : b, vals[0])
    return { min, max, rng: (max - min) || 1 }
  }
  const pctBounds   = axisBounds(slicedDatasets, 'y-pct')
  const otherBounds = axisBounds(slicedDatasets, 'y-other')
  const hasPctAxis   = pctBounds   != null
  const hasOtherAxis = otherBounds != null
  const dualAxis     = hasPctAxis && hasOtherAxis

  // Dynamic unit labels for the Y sliders — read from the actual series units
  const visibleDs = slicedDatasets.filter(d => !d.hidden)
  const pctSliderUnit   = [...new Set(visibleDs.filter(d => d.yAxisID === 'y-pct').map(d => d.unit ?? '').filter(Boolean))].join(' · ')   || '%'
  const otherSliderUnit = [...new Set(visibleDs.filter(d => d.yAxisID === 'y-other').map(d => d.unit ?? '').filter(Boolean))].join(' · ') || '#'

  // Apply slider percentages to each axis' real bounds
  const pctMin  = hasPctAxis   ? pctBounds.min   + (yRange[0]  / 100) * pctBounds.rng   : 0
  const pctMax  = hasPctAxis   ? pctBounds.min   + (yRange[1]  / 100) * pctBounds.rng   : 100
  const otherMin = hasOtherAxis ? otherBounds.min + (y2Range[0] / 100) * otherBounds.rng : 0
  const otherMax = hasOtherAxis ? otherBounds.min + (y2Range[1] / 100) * otherBounds.rng : 100

  // Bounds passed to Chart.js only when sliders are active
  const yBounds = sliders ? {
    pctMin:   hasPctAxis   ? pctMin   : undefined,
    pctMax:   hasPctAxis   ? pctMax   : undefined,
    otherMin: hasOtherAxis ? otherMin : undefined,
    otherMax: hasOtherAxis ? otherMax : undefined,
  } : {}

  const options = buildOptions(unit, yBounds, false, slicedDatasets)

  // Legacy single-axis values still used by UI labels: prefer whichever axis is active
  const yMin = hasPctAxis ? pctMin : otherMin
  const yMax = hasPctAxis ? pctMax : otherMax

  return (
    <>
      <Box sx={{ bgcolor: '#fff', border: '1px solid #e3e8ef', borderRadius: '12px', p: '12px 14px 10px', boxShadow: '0 1px 3px rgba(0,0,0,.04)', ...(fillHeight && { height: '100%', display: 'flex', flexDirection: 'column' }) }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1, position: 'relative' }}>
          <Typography onClick={() => setOpen(true)} sx={{ fontSize: 11, fontWeight: 600, color: '#4a5568', textAlign: 'center', cursor: 'pointer', '&:hover': { color: 'primary.main' } }}>
            {title}
          </Typography>
          <Box sx={{ position: 'absolute', right: 0, display: 'flex', gap: 0.25 }}>
            <IconButton size="small" onClick={() => setSliders(v => !v)}
              sx={{ p: 0.25, color: sliders ? 'primary.main' : 'text.disabled', '&:hover': { color: 'primary.main' } }}>
              <TuneIcon sx={{ fontSize: 15 }} />
            </IconButton>
            <IconButton size="small" onClick={() => setOpen(true)}
              sx={{ p: 0.25, color: 'text.disabled', '&:hover': { color: 'primary.main' } }}>
              <FullscreenIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Box>
        </Box>
        <Box
          ref={chartBoxRef}
          onClick={e => {
            const chart = chartRef.current?.chartInstance
            if (!chart || !chartBoxRef.current) return
            const rect  = chartBoxRef.current.getBoundingClientRect()
            const clickX = e.clientX - rect.left
            const clickY = e.clientY - rect.top
            const pts   = chart.getElementsAtXAxis(e.nativeEvent)
            if (!pts?.length) {
              const ci = chartRef.current?.chartInstance
              if (ci) { ci.options.tooltips.enabled = true; ci.update(0) }
              setPinnedTooltip(null)
              return
            }
            const idx   = pts[0]._index
            const label = chart.data.labels[idx]
            const ptX   = pts[0]._view?.x ?? clickX
            const ptY   = pts[0]._view?.y ?? clickY
            const items = chart.data.datasets
              .filter(ds => !ds.hidden)
              .map(ds => ({ key: ds.label, value: ds.data[idx], color: ds.borderColor }))
              .filter(it => it.value != null)
            const ci = chartRef.current?.chartInstance
            if (ci) { ci.options.tooltips.enabled = false; ci.update(0) }
            setPinnedTooltip({ label, items, x: ptX, y: ptY })
          }}
          sx={{ position: 'relative', height }}>
          {/* Watermark */}
          <Box sx={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none', zIndex: 0,
            width: '40%', display: 'flex', justifyContent: 'center', opacity: 0.05,
          }}>
            <img src={celfinetLogo} alt="" style={{ width: '100%' }} />
          </Box>
          <Line ref={chartRef} data={{ labels: slicedLabels, datasets: slicedDatasets }} options={options} />
          {pinnedTooltip && (
            <Box sx={{
              position: 'absolute',
              left: Math.min(pinnedTooltip.x + 8, 999),
              top:  Math.max(pinnedTooltip.y - 20, 0),
              transform: pinnedTooltip.x > 150 ? 'translateX(-100%)' : 'none',
              zIndex: 10, pointerEvents: 'auto',
              bgcolor: 'rgba(255,255,255,0.97)', border: '1px solid', borderColor: 'divider',
              borderRadius: 1.5, p: '8px 10px', boxShadow: '0 4px 12px rgba(0,0,0,.15)',
              minWidth: 130,
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'text.secondary' }}>
                  {pinnedTooltip.label}
                </Typography>
                <Box onClick={e => {
                  e.stopPropagation()
                  const ci = chartRef.current?.chartInstance
                  if (ci) { ci.options.tooltips.enabled = true; ci.update(0) }
                  setPinnedTooltip(null)
                }}
                  sx={{ fontSize: 11, cursor: 'pointer', color: 'text.disabled', ml: 1, lineHeight: 1, '&:hover': { color: 'text.primary' } }}>
                  ✕
                </Box>
              </Box>
              {pinnedTooltip.items.map(it => (
                <Box key={it.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: '1px' }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: it.color, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 10, color: 'text.secondary', flex: 1 }}>{it.key}</Typography>
                  <Typography sx={{ fontSize: 10, fontWeight: 600, color: 'text.primary' }}>
                    {Number(it.value).toFixed(2)}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>

              {sliders && totalPts > 0 && (
          <Box sx={{ px: 1.5, pt: 0.5, pb: 0.5 }}>
            {/* Header: "Reset all" button — disabled when nothing has been moved */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 0.25 }}>
              <Button
                size="small"
                onClick={resetAll}
                disabled={allReset}
                startIcon={<RestartAltIcon sx={{ fontSize: 13 }} />}
                sx={{
                  py: 0.25, px: 0.75, fontSize: 9, textTransform: 'none',
                  color: allReset ? 'text.disabled' : 'text.secondary',
                  '&:hover': { color: 'primary.main', bgcolor: 'transparent' },
                }}
              >
                Reset all
              </Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            {/* X slider */}
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}>
                <Typography sx={{ fontSize: 9, color: 'text.disabled', flex: 1 }}>X — Date range</Typography>
                <ResetButton onClick={resetX} disabled={isXReset} />
              </Box>
              <Slider value={range} onChange={(_, v) => handleRangeChange(v)} size="small" disableSwap
                sx={{ color: 'primary.main', height: 3, '& .MuiSlider-thumb': { width: 10, height: 10 }, '& .MuiSlider-rail': { opacity: 0.2 } }}
              />
              <Typography sx={{ fontSize: 9, color: 'text.disabled', textAlign: 'center' }}>
                {slicedLabels[0] ?? ''} → {slicedLabels[slicedLabels.length - 1] ?? ''}
              </Typography>
            </Box>
            {/* Y1 slider — % axis (always when % data exists) */}
            {hasPctAxis && (
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}>
                  <Typography sx={{ fontSize: 9, color: 'text.disabled', flex: 1 }}>
                    Y1 — Range {dualAxis ? `(${pctSliderUnit})` : ''}
                  </Typography>
                  <ResetButton onClick={resetY1} disabled={isY1Reset} />
                </Box>
                <Slider value={yRange} onChange={(_, v) => setYRange(v)} size="small" disableSwap
                  sx={{ color: 'secondary.main', height: 3, '& .MuiSlider-thumb': { width: 10, height: 10 }, '& .MuiSlider-rail': { opacity: 0.2 } }}
                />
                <Typography sx={{ fontSize: 9, color: 'text.disabled', textAlign: 'center' }}>
                  {pctMin.toFixed(2)} → {pctMax.toFixed(2)}
                </Typography>
              </Box>
            )}
            {/* Y2 slider — other axis (#/erl/MB), only when dual axis */}
            {dualAxis && (
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}>
                  <Typography sx={{ fontSize: 9, color: 'text.disabled', flex: 1 }}>Y2 — Range ({otherSliderUnit})</Typography>
                  <ResetButton onClick={resetY2} disabled={isY2Reset} />
                </Box>
                <Slider value={y2Range} onChange={(_, v) => setY2Range(v)} size="small" disableSwap
                  sx={{ color: 'warning.main', height: 3, '& .MuiSlider-thumb': { width: 10, height: 10 }, '& .MuiSlider-rail': { opacity: 0.2 } }}
                />
                <Typography sx={{ fontSize: 9, color: 'text.disabled', textAlign: 'center' }}>
                  {Number(otherMin).toLocaleString(undefined, { maximumFractionDigits: 0 })} → {Number(otherMax).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </Typography>
              </Box>
            )}
            {/* Fallback: only non-% axis exists */}
            {!hasPctAxis && hasOtherAxis && (
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}>
                  <Typography sx={{ fontSize: 9, color: 'text.disabled', flex: 1 }}>Y — Range ({otherSliderUnit})</Typography>
                  <ResetButton onClick={resetY2} disabled={isY2Reset} />
                </Box>
                <Slider value={y2Range} onChange={(_, v) => setY2Range(v)} size="small" disableSwap
                  sx={{ color: 'warning.main', height: 3, '& .MuiSlider-thumb': { width: 10, height: 10 }, '& .MuiSlider-rail': { opacity: 0.2 } }}
                />
                <Typography sx={{ fontSize: 9, color: 'text.disabled', textAlign: 'center' }}>
                  {Number(otherMin).toLocaleString(undefined, { maximumFractionDigits: 0 })} → {Number(otherMax).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </Typography>
              </Box>
            )}
            </Box>
          </Box>
        )}
      </Box>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xl" fullWidth
        PaperProps={{ sx: { borderRadius: '16px', m: 2 } }}>
        <DialogContent sx={{ p: '20px 24px 24px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#2d3748' }}>{title}</Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title="Toggle sliders">
                <IconButton size="small" onClick={() => setSliders(v => !v)}
                  sx={{ color: sliders ? 'primary.main' : 'text.disabled', '&:hover': { color: 'primary.main' } }}>
                  <TuneIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Export chart as PDF">
                <IconButton size="small" onClick={exportChartPDF} sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
                  <DownloadIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              <IconButton size="small" onClick={() => {
                setOpen(false)
                setModalPinnedTooltip(null)
                const ci = modalLineRef.current?.chartInstance
                if (ci) { ci.options.tooltips.enabled = true; ci.update(0) }
              }} sx={{ color: 'text.disabled' }}>
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '65vh' }}>
            {/* Modal sliders */}
            {sliders && (
              <Box sx={{ px: 1, pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 0.25 }}>
                  <Button
                    size="small"
                    onClick={resetAll}
                    disabled={allReset}
                    startIcon={<RestartAltIcon sx={{ fontSize: 13 }} />}
                    sx={{
                      py: 0.25, px: 0.75, fontSize: 9, textTransform: 'none',
                      color: allReset ? 'text.disabled' : 'text.secondary',
                      '&:hover': { color: 'primary.main', bgcolor: 'transparent' },
                    }}
                  >
                    Reset all
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}>
                    <Typography sx={{ fontSize: 9, color: 'text.disabled', flex: 1 }}>X — Date range</Typography>
                    <ResetButton onClick={resetX} disabled={isXReset} />
                  </Box>
                  <Slider value={range} onChange={(_, v) => handleRangeChange(v)} size="small" disableSwap
                    sx={{ color: 'primary.main', height: 3, '& .MuiSlider-thumb': { width: 10, height: 10 }, '& .MuiSlider-rail': { opacity: 0.2 } }} />
                  <Typography sx={{ fontSize: 9, color: 'text.disabled', textAlign: 'center' }}>
                    {slicedLabels[0] ?? ''} → {slicedLabels[slicedLabels.length - 1] ?? ''}
                  </Typography>
                </Box>
                {hasPctAxis && (
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}>
                      <Typography sx={{ fontSize: 9, color: 'text.disabled', flex: 1 }}>
                        Y1 — Range {dualAxis ? `(${pctSliderUnit})` : ''}
                      </Typography>
                      <ResetButton onClick={resetY1} disabled={isY1Reset} />
                    </Box>
                    <Slider value={yRange} onChange={(_, v) => setYRange(v)} size="small" disableSwap
                      sx={{ color: 'secondary.main', height: 3, '& .MuiSlider-thumb': { width: 10, height: 10 }, '& .MuiSlider-rail': { opacity: 0.2 } }} />
                    <Typography sx={{ fontSize: 9, color: 'text.disabled', textAlign: 'center' }}>
                      {pctMin.toFixed(2)} → {pctMax.toFixed(2)}
                    </Typography>
                  </Box>
                )}
                {dualAxis && (
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}>
                      <Typography sx={{ fontSize: 9, color: 'text.disabled', flex: 1 }}>Y2 — Range ({otherSliderUnit})</Typography>
                      <ResetButton onClick={resetY2} disabled={isY2Reset} />
                    </Box>
                    <Slider value={y2Range} onChange={(_, v) => setY2Range(v)} size="small" disableSwap
                      sx={{ color: 'warning.main', height: 3, '& .MuiSlider-thumb': { width: 10, height: 10 }, '& .MuiSlider-rail': { opacity: 0.2 } }} />
                    <Typography sx={{ fontSize: 9, color: 'text.disabled', textAlign: 'center' }}>
                      {Number(otherMin).toLocaleString(undefined, { maximumFractionDigits: 0 })} → {Number(otherMax).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </Typography>
                  </Box>
                )}
                {!hasPctAxis && hasOtherAxis && (
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}>
                      <Typography sx={{ fontSize: 9, color: 'text.disabled', flex: 1 }}>Y — Range ({otherSliderUnit})</Typography>
                      <ResetButton onClick={resetY2} disabled={isY2Reset} />
                    </Box>
                    <Slider value={y2Range} onChange={(_, v) => setY2Range(v)} size="small" disableSwap
                      sx={{ color: 'warning.main', height: 3, '& .MuiSlider-thumb': { width: 10, height: 10 }, '& .MuiSlider-rail': { opacity: 0.2 } }} />
                    <Typography sx={{ fontSize: 9, color: 'text.disabled', textAlign: 'center' }}>
                      {Number(otherMin).toLocaleString(undefined, { maximumFractionDigits: 0 })} → {Number(otherMax).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </Typography>
                  </Box>
                )}
                </Box>
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 2, flex: 1, minHeight: 0 }}>
            <Box
              ref={modalChartBoxRef}
              onClick={e => {
                const ci = modalLineRef.current?.chartInstance
                if (!ci || !modalChartBoxRef.current) return
                const rect  = modalChartBoxRef.current.getBoundingClientRect()
                const clickX = e.clientX - rect.left
                const clickY = e.clientY - rect.top
                const pts   = ci.getElementsAtXAxis(e.nativeEvent)
                if (!pts?.length) {
                  ci.options.tooltips.enabled = true; ci.update(0)
                  setModalPinnedTooltip(null); return
                }
                const idx   = pts[0]._index
                const label = ci.data.labels[idx]
                const ptX   = pts[0]._view?.x ?? clickX
                const ptY   = pts[0]._view?.y ?? clickY
                const items = ci.data.datasets
                  .filter(ds => !ds.hidden)
                  .map(ds => ({ key: ds.label, value: ds.data[idx], color: ds.borderColor }))
                  .filter(it => it.value != null)
                ci.options.tooltips.enabled = false; ci.update(0)
                setModalPinnedTooltip({ label, items, x: ptX, y: ptY })
              }}
              sx={{ flex: 1, position: 'relative' }}>
              {/* Watermark */}
              <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', pointerEvents: 'none', zIndex: 0, width: '40%', display: 'flex', justifyContent: 'center', opacity: 0.05 }}>
                <img src={celfinetLogo} alt="" style={{ width: '100%' }} />
              </Box>
              <Line ref={modalLineRef} data={{ labels: slicedLabels, datasets: [...slicedDatasets] }} options={buildOptions(unit, yBounds, false, slicedDatasets)} />
              {modalPinnedTooltip && (
                <Box sx={{
                  position: 'absolute',
                  left: modalPinnedTooltip.x + 8,
                  top:  Math.max(modalPinnedTooltip.y - 20, 0),
                  transform: modalPinnedTooltip.x > 300 ? 'translateX(-100%)' : 'none',
                  zIndex: 10, pointerEvents: 'auto',
                  bgcolor: 'rgba(255,255,255,0.97)', border: '1px solid', borderColor: 'divider',
                  borderRadius: 1.5, p: '8px 10px', boxShadow: '0 4px 12px rgba(0,0,0,.15)', minWidth: 140,
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary' }}>{modalPinnedTooltip.label}</Typography>
                    <Box onClick={e => {
                      e.stopPropagation()
                      const ci = modalLineRef.current?.chartInstance
                      if (ci) { ci.options.tooltips.enabled = true; ci.update(0) }
                      setModalPinnedTooltip(null)
                    }} sx={{ fontSize: 12, cursor: 'pointer', color: 'text.disabled', ml: 1, '&:hover': { color: 'text.primary' } }}>✕</Box>
                  </Box>
                  {modalPinnedTooltip.items.map(it => (
                    <Box key={it.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: '1px' }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: it.color, flexShrink: 0 }} />
                      <Typography sx={{ fontSize: 11, color: 'text.secondary', flex: 1 }}>{it.key}</Typography>
                      <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.primary' }}>{Number(it.value).toFixed(2)}</Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
            <ModalLegend
              series={series}
              visibleSeries={visibleSeries}
              onToggle={handleToggle}
              onClear={() => dispatch(resetBeacon4HiddenSeries())}
              hasRag={!!kpiKey}
            />
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  )
}